"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import {
  mockSignIn,
  mockSignUp,
  mockSignOut,
  mockIsAuthenticated,
  mockGetUserInfo,
} from "@/lib/auth-mock";

interface AuthContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  userEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 初期ロード時に認証状態を確認
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await mockIsAuthenticated();
        setIsLoggedIn(authenticated);

        if (authenticated) {
          const userInfo = await mockGetUserInfo();
          setUserEmail(userInfo?.email || null);
        }
      } catch (err) {
        console.error("認証チェックエラー:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // ログイン処理
  const login = async (email: string, password: string) => {
    setError(null);
    try {
      await mockSignIn(email, password);
      setIsLoggedIn(true);
      setUserEmail(email);
    } catch (err: unknown) {
      console.error("ログインエラー:", err);
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    }
  };

  // 登録処理
  const register = async (email: string, password: string) => {
    setError(null);
    try {
      await mockSignUp(email, password);
      // 登録後は自動ログインしない（確認コードが必要な場合があるため）
    } catch (err: unknown) {
      console.error("登録エラー:", err);
      setError(err instanceof Error ? err.message : "Registration failed");
      throw err;
    }
  };

  // ログアウト処理
  const logout = () => {
    mockSignOut();
    setIsLoggedIn(false);
    setUserEmail(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        userEmail,
        login,
        register,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
